import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { Button, Modal } from "../components/Modal";
import * as UserStore from "./UserStore";

export type UserLifecycleAction = "archive" | "delete";

interface UserLifecycleModalProps {
    action: UserLifecycleAction | null;
    user: any;
    onClose: () => void;
    onCompleted: () => void;
}

export const UserLifecycleModal = ({ action, user, onClose, onCompleted }: UserLifecycleModalProps) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        setIsSubmitting(false);
        setError("");
    }, [action, user?.id]);

    if (!action || !user) return null;

    const isDelete = action === "delete";
    const submit = () => {
        setIsSubmitting(true);
        setError("");

        const request = isDelete
            ? UserStore.DeleteUserFromOrganization(user.id)
            : UserStore.ArchiveUser(user.id);

        request
            .then(() => {
                setIsSubmitting(false);
                onCompleted();
            })
            .catch((response) => {
                setError(response.response?.data?.message || `Unable to ${action} this user.`);
                setIsSubmitting(false);
            });
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            closeOnOverlayClick={!isSubmitting}
            title={isDelete ? "Delete User From Organization" : "Archive User"}
            icon={["fad", isDelete ? "trash" : "archive"]}
            iconColor={isDelete ? "text-red-600" : "text-amber-600"}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button variant={isDelete ? "danger" : "warning"} onClick={submit} disabled={isSubmitting}>
                        {isSubmitting && <FontAwesomeIcon icon={["fad", "spinner"]} className="w-4 h-4 mr-2 animate-spin" />}
                        {isDelete ? "Delete memberships" : "Archive user"}
                    </Button>
                </>
            }
        >
            <div className="p-6 space-y-4">
                <p className="text-sm text-gray-700">
                    <span className="font-semibold">{user.fullName || user.email || user.id}</span>
                    {isDelete
                        ? " will have all membership records for the current organization permanently removed. Their identity and memberships in other organizations will remain intact."
                        : " will lose access to the current organization. The disabled membership records are retained and can be restored later."}
                </p>
                {isDelete && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                        This cannot be undone from the users page. Global identity deletion remains restricted to the orphaned-account maintenance flow.
                    </div>
                )}
                {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
            </div>
        </Modal>
    );
};
